import { useState } from 'react';
import { Link } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface Row {
  companyName: string;
  pdfUrl: string;
  signedOn: string | null;
  label: string | null;
}

interface ImportResultRow {
  index: number;
  companyName: string;
  pdfUrl: string;
  outcome: 'imported' | 'duplicate' | 'pdf_failed' | 'wrong_url' | 'error';
  organizationId: string | null;
  organizationCreated: boolean;
  attachmentId: string | null;
  startedOnSet: boolean;
  message: string | null;
}

interface ImportResponse {
  summary: {
    total: number;
    imported: number;
    duplicates: number;
    pdfFailed: number;
    wrongUrl: number;
    errors: number;
    orgsCreated: number;
    startedOnSet: number;
  };
  results: ImportResultRow[];
}

const OUTCOME_PILL: Record<ImportResultRow['outcome'], string> = {
  imported: 'bg-emerald-50 text-emerald-700',
  duplicate: 'bg-[var(--color-surface)] text-[var(--color-muted)]',
  pdf_failed: 'bg-red-50 text-red-700',
  wrong_url: 'bg-red-50 text-red-700',
  error: 'bg-red-50 text-red-700',
};

const OUTCOME_LABEL: Record<ImportResultRow['outcome'], string> = {
  imported: 'imported',
  duplicate: 'already imported',
  pdf_failed: 'PDF fetch failed',
  wrong_url: 'wrong URL',
  error: 'error',
};

// Parse the user's pasted text. Each non-empty line should be:
//   companyName <delim> url [<delim> YYYY-MM-DD [<delim> label]]
// Delim = tab, comma, or pipe. Lines starting with # are comments.
function parsePaste(text: string): { rows: Row[]; errors: string[] } {
  const rows: Row[] = [];
  const errors: string[] = [];
  const lines = text.split(/\r?\n/);
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!.trim();
    if (!line || line.startsWith('#')) continue;
    const parts = line.split(/\t|,|\|/).map((s) => s.trim());
    if (parts.length < 2) {
      errors.push(`Line ${i + 1}: expected "Company<TAB>url" — got: ${line.slice(0, 80)}`);
      continue;
    }
    const [companyName, pdfUrl, signedOn, label] = parts;
    if (!companyName || !pdfUrl) {
      errors.push(`Line ${i + 1}: missing company or url`);
      continue;
    }
    if (!/^https?:\/\//i.test(pdfUrl)) {
      errors.push(`Line ${i + 1}: url must start with http(s)://`);
      continue;
    }
    if (signedOn && !/^\d{4}-\d{2}-\d{2}$/.test(signedOn)) {
      errors.push(`Line ${i + 1}: signed-on date must be YYYY-MM-DD`);
      continue;
    }
    rows.push({
      companyName,
      pdfUrl,
      signedOn: signedOn || null,
      label: label || null,
    });
  }
  return { rows, errors };
}

export function AdminQwilrImportPage(): React.ReactElement {
  useDocumentTitle('Qwilr URL import');
  const { agent } = useAuth();

  const [text, setText] = useState('');
  const [running, setRunning] = useState(false);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [data, setData] = useState<ImportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setError(null);
    setData(null);
    const { rows, errors } = parsePaste(text);
    setParseErrors(errors);
    if (rows.length === 0) {
      setError('Nothing to import — paste at least one valid line.');
      return;
    }
    setRunning(true);
    try {
      const r = await apiFetch<ImportResponse>('/api/admin/qwilr/import-urls', {
        method: 'POST',
        json: { rows },
      });
      setData(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Import failed.');
    } finally {
      setRunning(false);
    }
  }

  if (!agent) return <p>Loading…</p>;
  if (agent.role !== 'admin') {
    return (
      <Card>
        <p className="text-[var(--color-muted)]">Admins only.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin" className="text-sm text-[var(--color-muted)] hover:underline">
          ← Admin
        </Link>
        <h1 className="mt-1 text-3xl font-bold text-[var(--color-header)]">
          Qwilr — bulk import (paste URLs)
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-[var(--color-muted)]">
          Paste one signed contract per line. Each line: company name, then the Qwilr public PDF
          URL, optionally followed by a signed-on date (YYYY-MM-DD) and a label. Use{' '}
          <strong>tab, comma, or pipe</strong> as separator. Lines starting with{' '}
          <code>#</code> are ignored.
        </p>
      </div>

      <Card>
        <label className="block">
          <span className="block text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]">
            Paste here
          </span>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={12}
            placeholder={[
              '# Examples (tab, comma, or pipe separated)',
              'Schema Networks\thttps://t3techpartners.qwilr.com/pdf/IVBpQEVbT0q2?orientation=portrait&compressionSetting=print&pageSize=USPAPER',
              'Acme Corp\thttps://t3techpartners.qwilr.com/pdf/AbC123XyZ?orientation=portrait\t2024-08-15\tMSA 2024',
            ].join('\n')}
            className="mt-2 w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 font-mono text-xs"
            spellCheck={false}
          />
        </label>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button onClick={() => void run()} disabled={running || !text.trim()}>
            {running ? 'Importing…' : 'Import'}
          </Button>
          {parseErrors.length > 0 && (
            <span className="text-xs text-amber-700">
              {parseErrors.length} line{parseErrors.length === 1 ? '' : 's'} skipped (see below)
            </span>
          )}
        </div>
      </Card>

      {parseErrors.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/40">
          <h3 className="text-sm font-semibold text-amber-900">Lines skipped:</h3>
          <ul className="mt-1 space-y-0.5 text-xs text-amber-900">
            {parseErrors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </Card>
      )}

      {error && (
        <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
          <p className="text-sm text-[var(--color-danger)]">{error}</p>
        </Card>
      )}

      {data && (
        <>
          <Card>
            <h2 className="text-base font-semibold text-[var(--color-header)]">Summary</h2>
            <dl className="mt-2 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
              <Stat label="Rows" value={data.summary.total} />
              <Stat label="Imported" value={data.summary.imported} />
              <Stat label="Already imported" value={data.summary.duplicates} />
              <Stat label="Orgs created" value={data.summary.orgsCreated} />
              <Stat label="started_on set" value={data.summary.startedOnSet} />
              <Stat label="Wrong URL" value={data.summary.wrongUrl} />
              <Stat label="PDF fetch failed" value={data.summary.pdfFailed} />
              <Stat label="Errors" value={data.summary.errors} />
            </dl>
          </Card>

          <Card className="overflow-hidden !p-0">
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                <tr>
                  <th className="px-4 py-3 w-12">#</th>
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3">URL</th>
                  <th className="px-4 py-3">Outcome</th>
                  <th className="px-4 py-3">Notes</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((r) => (
                  <tr key={r.index} className="border-t border-[var(--color-border)]">
                    <td className="px-4 py-2 font-mono text-xs text-[var(--color-muted)]">{r.index}</td>
                    <td className="px-4 py-2">
                      {r.organizationId ? (
                        <Link
                          to={`/organizations/${r.organizationId}`}
                          className="text-[var(--color-t3-blue)] hover:underline"
                        >
                          {r.companyName}
                        </Link>
                      ) : (
                        r.companyName
                      )}
                      {r.organizationCreated && (
                        <span className="ml-1 rounded-full bg-[var(--color-t3-blue)]/10 px-1.5 py-0.5 text-[10px] text-[var(--color-t3-blue)]">
                          new
                        </span>
                      )}
                    </td>
                    <td className="max-w-[280px] truncate px-4 py-2 font-mono text-[10px] text-[var(--color-muted)]">
                      {r.pdfUrl}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs ${OUTCOME_PILL[r.outcome]}`}>
                        {OUTCOME_LABEL[r.outcome]}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-[var(--color-muted)]">
                      {r.message ?? (r.startedOnSet ? 'started_on backfilled' : '')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }): React.ReactElement {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-[var(--color-muted)]">{label}</div>
      <div className="mt-0.5 text-lg font-semibold text-[var(--color-header)]">{value}</div>
    </div>
  );
}
